// Shared SRS station layout for the interactive HighTension modules
// ("Antrenare" / training and "Invatare" / learning).
//
// The whole double-busbar station is authored once here and painted onto the
// grid through the `addSign` callback the calling page hands in. Alongside the
// drawing it returns:
//   - cellMeta   : per-cell switch registry (bar separators, line separator,
//                  breaker, CLP, generator) used to init a start state, compare
//                  against a target state and attribute a click to a cell
//   - maneuvers  : the interlock tables (see the Maneuvers doc below)
//
// Extracted from HighTensionTraining.tsx so the learning module paints exactly
// the same station with exactly the same interlocks.

import SCHEME_ELEMENTS from "./getElements";

// Custom canvas: wider than A3 so bays get horizontal breathing room and the
// scheme's aspect ratio (~53:28 = 1.9) matches a 16:9 screen -> fills the page.
export const GRID_COLS = 53;
export const GRID_ROWS = 28;

const S: any = SCHEME_ELEMENTS;

// The switch images the modules swap between.
export const IMG = {
    sepOpen: S["separator_inalta_tensiune_deschis"].imageSource,
    sepClosed: S["separator_inalta_tensiune_inchis"].imageSource,
    clpOpen: S["separator_clp_inalta_tensiune_deschis"].imageSource,
    clpClosed: S["separator_clp_inalta_tensiune_inchis"].imageSource,
    intrOpen: S["intreruptor_inalta_tensiune_deschis"].imageSource,
    intrClosed: S["intreruptor_inalta_tensiune_inchis"].imageSource,
    genOff: S["generator_inalta_tensiune"].imageSource,
    genOn: S["generator_inalta_pornit"].imageSource,
};

// Per-cell switch registry: cellId -> the coords of its bar separators (with
// which bar they tap), line separator, breaker, CLP and generator.
export type CellSwitches = {
    barSeps: { bar: "I" | "II", cell: [number, number] }[],
    lineSep?: [number, number],
    breaker?: [number, number],
    clp?: [number, number],
    generator?: [number, number],
};

// Maneuver interlocks, populated while the layout is built:
//  - breakers: separator cell -> breaker cell(s) that must be OPEN before
//    the separator may be operated (separators never switch under load)
//  - clp: switch/generator cell -> its bay's CLP cell; closing/starting is
//    forbidden while that CLP (earthing knife) is closed
//  - clpIsolators: CLP cell -> separator(s) that isolate the section it
//    grounds; per NTE 009 the CLP may close only on a visibly separated
//    element, so those separators must be OPEN first (not just "no current")
//  - pair: bar separator -> the other bar separator of the same bay; both
//    closed would couple the two busbars through the bay -> forbidden
//  - closeRequires: switch -> switches that must already be closed first
//    (closing order: bar sep -> line sep -> breaker; C.L./C.T.V: both seps
//    -> breaker)
//  - openRequires: switch -> switches that must already be open first
//    (opening runs in the reverse order)
//  - parallelMsg: breaker cell -> text for the parallel-conditions dialog
//    shown when closing it couples two energized sections
//  - clpBarLine / barLineSeps / sepBarLine / barLineClp: busbar earthing
//    (C.M. cells only). The CLP grounds a whole busbar section (IB / IA /
//    IIB / IIA); clpBarLine maps a C.M. CLP cell -> its busbar, barLineSeps
//    lists every bar separator tapping that busbar, sepBarLine is the reverse
//    (bar separator -> busbar) and barLineClp maps a busbar -> its C.M. CLP.
export interface Maneuvers {
    breakers: Record<string, Array<[number, number]>>;
    clp: Record<string, [number, number]>;
    clpIsolators: Record<string, Array<[number, number]>>;
    pair: Record<string, { cell: [number, number], msg: string }>;
    closeRequires: Record<string, { cells: Array<[number, number]>, all?: boolean, msg: string }>;
    openRequires: Record<string, { cells: Array<[number, number]>, msg: string }>;
    parallelMsg: Record<string, string>;
    clpBarLine: Record<string, string>;
    barLineSeps: Record<string, Array<[number, number]>>;
    sepBarLine: Record<string, string>;
    barLineClp: Record<string, [number, number]>;
}

export const emptyManeuvers = (): Maneuvers => ({
    breakers: {}, clp: {}, clpIsolators: {}, pair: {}, closeRequires: {},
    openRequires: {}, parallelMsg: {}, clpBarLine: {}, barLineSeps: {},
    sepBarLine: {}, barLineClp: {},
});

export interface SRSLayout { cellMeta: Record<string, CellSwitches>; maneuvers: Maneuvers; }

export type AddSign = (x: number, y: number, page: number, img: any, flip: number, texts: any[]) => void;

/**
 * The full SRS double-busbar station, mirroring the hardcoded SRS.jsx diagram:
 *
 *  - bar I drawn twice (top row for the upper bays, bottom row for the lower
 *    bays) and tied at the ends; bar II runs between them
 *  - each side split in a B (left) and A (right) section, bridged by the two
 *    longitudinal couplings C.L. II B - II A (over the top) and
 *    C.L. I B - I A (under the bottom)
 *  - upper bays: Trafo 4T / 3T / 2T and the C.M. measuring cells
 *  - lower bays: OE consumers, LEA Donalam, C.T.V cells, the two generators and
 *    the C.M. II measuring cells
 *
 * Where a bay branch crosses a bar without touching it, the cell stacks a
 * "podet" (hop) conductor over a vertical conductor: the two names keep
 * independent electrical nets in the engine.
 *
 * Every switch starts open (green); the caller then initializes the wanted
 * start state through `setCellState`.
 *
 * `DY` shifts the whole scheme vertically; the interlock tables are remapped by
 * the same offset at the end so clicks (which read the shifted grid rows) match.
 */
export function buildSRSLayout(addSign: AddSign, DY = 0): SRSLayout {
    const P = 1;
    const COND = S["conductor_lung"].imageSource;
    const PODET = S["conductor_lung_podet"].imageSource;
    const DERIV = S["derivatie_din_conductor"].imageSource;
    // Corners by the arms actually drawn in the svg (names are misleading):
    const CORNER_TR = S["colt_stanga_jos"].imageSource;   // "L"  arms top + right
    const CORNER_TL = S["colt_dreapta_jos"].imageSource;  // "J"  arms top + left
    const CORNER_BR = S["colt_stanga_sus"].imageSource;   // "r"  arms bottom + right
    const CORNER_BL = S["colt_dreapta_sus"].imageSource;  // "?"  arms bottom + left
    // Derivatie arms per flip: 0 = left+right+bottom, 90 = left+top+bottom,
    // 180 = left+right+top, 270 = right+top+bottom.
    const SEP = S["separator_inalta_tensiune_deschis"].imageSource;
    const CLP = S["separator_clp_inalta_tensiune_deschis"].imageSource;
    const INTR = S["intreruptor_inalta_tensiune_deschis"].imageSource;
    const GEN = S["generator_inalta_tensiune"].imageSource;
    const TRAFO = S["consumator_inalta_tensiune"].imageSource;
    const CEL_MAS = S["celula_de_masura_inalta_tensiune"].imageSource;
    // Arrows: source arrow (feeds the circuit) for LEA feeders + generators,
    // sink arrow ("final") for consumers. Arrow glyph points right at flip 0,
    // so flip 270 = up (toward exterior on top bays), flip 90 = down.
    const ARROW = S["conductor_lung_sageata"].imageSource;
    const ARROW_FINAL = S["conductor_lung_sageata_final"].imageSource;

    const BUS_TOP = 13, BUS_MID = 14, BUS_BOT = 15;

    const used = new Set<string>();
    const put = (x: number, y: number, img: any, flip = 0, texts: any[] = []) => {
        used.add(`${x}_${y}`);
        addSign(x, y + DY, P, img, flip, texts);
    };
    // Bar crossing without contact: horizontal hop + vertical pass-through,
    // two stacked names = two independent nets.
    const putHop = (x: number, y: number) => {
        used.add(`${x}_${y}`);
        addSign(x, y + DY, P, [{ name: PODET, flip: 0 }, { name: COND, flip: 90 }], 0, []);
    };
    // NOTE: positionX / positionY are interpreted in mm by zoomFunction
    // (one grid cell is ~10mm on A3); fontSize renders at 0.75x px.
    const lbl2 = (l1: string, l2: string, dy: number) => ([
        { name: l1, positionX: 4, positionY: dy, flip: 0, fontSize: 22, fontWeight: 700, centered: true },
        { name: l2, positionX: 4, positionY: dy + 6, flip: 0, fontSize: 16, fontWeight: 600, centered: true },
    ]);
    const lbl1 = (t: string, dx: number, dy: number) => ([
        { name: t, positionX: dx, positionY: dy, flip: 0, fontSize: 26, fontWeight: 800 },
    ]);

    const M: Maneuvers = emptyManeuvers();
    const cellMeta: Record<string, CellSwitches> = {};
    // Register a cell's switch coords (used for start-state init, final-state
    // comparison, logging and the unnecessary-maneuver check).
    const regCell = (name: string, meta: CellSwitches) => { cellMeta[name] = meta; };

    // Busbar section of a column: B = left of the couplings, A = right.
    const sec = (col: number) => col < 26 ? "B" : "A";
    // Register a bar separator as tapping a given busbar line (IB/IA/IIB/IIA).
    const regSep = (sx: number, sy: number, lineName: string) => {
        (M.barLineSeps[lineName] = M.barLineSeps[lineName] || []).push([sx, sy]);
        M.sepBarLine[`${sx}_${sy}`] = lineName;
    };

    // ---- Upper bay: symbol -> breaker -> fork -> 2 bar separators -> bars ----
    const topBay = (C: number, name: string, cel: string, opts: { head?: any, headFlip?: number, singleBar?: boolean } = {}) => {
        // Head defaults to the consumer symbol (body up, lead down into the
        // bay). Bar separators on row 8, breaker on row 6.
        const { head = TRAFO, headFlip = 0, singleBar = false } = opts;
        // Head on row 4 (aligned with the C.M. cells); one conductor between it
        // and the breaker, then (fork ->) bar separator(s) -> run to the bars.
        put(C, 3, S["conductor_lung_sageata_final_sus"].imageSource, 270);   // outgoing-line arrow, raised in-cell above the consumer
        put(C, 4, head, headFlip, lbl2(name, cel, -22));
        put(C, 5, COND, 90);                    // line between symbol and breaker
        put(C, 6, INTR);
        if (singleBar) {
            // One tie to the system: straight down column C to a single bar
            // separator, then onto bar I only.
            M.breakers[`${C}_8`] = [[C, 6]];
            M.closeRequires[`${C}_6`] = { cells: [[C, 8]], msg: `Nu se poate închide întreruptorul de pe linia ${name} dacă separatorul de bară nu este închis!` };
            put(C, 7, COND, 90);
            put(C, 8, SEP);
            regSep(C, 8, "I" + sec(C));
            regCell(name, { barSeps: [{ bar: "I", cell: [C, 8] }], breaker: [C, 6] });
            for (let y = 9; y <= 12; y++) put(C, y, COND, 90);
            put(C, BUS_TOP, DERIV, 180);        // junction on bar I (top run)
            return;
        }
        M.breakers[`${C - 1}_8`] = [[C, 6]];
        M.breakers[`${C + 1}_8`] = [[C, 6]];
        const pairMsg = `Nu se poate închide separatorul de bară de pe linia ${name} dacă celălalt separator de bară este închis!`;
        M.pair[`${C - 1}_8`] = { cell: [C + 1, 8], msg: pairMsg };
        M.pair[`${C + 1}_8`] = { cell: [C - 1, 8], msg: pairMsg };
        M.closeRequires[`${C}_6`] = { cells: [[C - 1, 8], [C + 1, 8]], msg: `Nu se poate închide întreruptorul de pe linia ${name} dacă separatorul de bară nu este închis!` };
        put(C, 7, DERIV, 180); put(C - 1, 7, CORNER_BR); put(C + 1, 7, CORNER_BL);
        put(C - 1, 8, SEP); put(C + 1, 8, SEP);
        regSep(C - 1, 8, "I" + sec(C)); regSep(C + 1, 8, "II" + sec(C));
        regCell(name, { barSeps: [{ bar: "I", cell: [C - 1, 8] }, { bar: "II", cell: [C + 1, 8] }], breaker: [C, 6] });
        for (let y = 9; y <= 12; y++) { put(C - 1, y, COND, 90); put(C + 1, y, COND, 90); }
        put(C - 1, BUS_TOP, DERIV, 180);        // junction on bar I (top run)
        putHop(C + 1, BUS_TOP);                 // crosses bar I to reach bar II
        put(C + 1, BUS_MID, DERIV, 180);        // junction on bar II
    };

    // ---- Lower bay: bars -> 2 bar separators -> merge -> breaker -> ... ----
    const bottomBay = (
        C: number, kind: "oe" | "gen" | "ctv", name: string, cel: string,
        opts: { crossRight?: boolean, endRight?: boolean } = {}
    ) => {
        const { crossRight = true, endRight = false } = opts;
        put(C - 1, BUS_BOT, DERIV, 0);          // junction on bar I (bottom run)
        // junction on bar II; endRight = the bar ends right here (last bay)
        put(C + 1, BUS_MID, endRight ? CORNER_BL : DERIV, 0);
        if (crossRight) putHop(C + 1, BUS_BOT); // crosses bar I to come from bar II
        else put(C + 1, BUS_BOT, COND, 90);
        put(C - 1, 16, SEP); put(C + 1, 16, SEP);
        regSep(C - 1, 16, "I" + sec(C)); regSep(C + 1, 16, "II" + sec(C));
        put(C - 1, 17, COND, 90); put(C + 1, 17, COND, 90);
        if (kind === "ctv") {
            // Two parallel branches joined at the bottom by a U, with the
            // breaker on the bar II branch (like the SRS drawing).
            M.breakers[`${C - 1}_16`] = [[C + 1, 19]];
            M.breakers[`${C + 1}_16`] = [[C + 1, 19]];
            M.closeRequires[`${C + 1}_19`] = { cells: [[C - 1, 16], [C + 1, 16]], all: true, msg: `Nu se poate închide întreruptorul de pe ${name} dacă separatoarele nu sunt închise!` };
            regCell(name, { barSeps: [{ bar: "I", cell: [C - 1, 16] }, { bar: "II", cell: [C + 1, 16] }], breaker: [C + 1, 19] });
            put(C - 1, 18, COND, 90); put(C - 1, 19, COND, 90); put(C - 1, 20, COND, 90);
            put(C + 1, 18, COND, 90);
            put(C + 1, 19, INTR);
            put(C + 1, 20, COND, 90);
            put(C - 1, 21, CORNER_TR);
            put(C, 21, COND, 0, lbl2(name, cel, 12));
            put(C + 1, 21, CORNER_TL);
            return;
        }
        M.breakers[`${C - 1}_16`] = [[C, 19]];
        M.breakers[`${C + 1}_16`] = [[C, 19]];
        M.breakers[`${C}_21`] = [[C, 19]];
        M.clp[`${C}_21`] = [C + 1, 23];
        M.clp[`${C}_19`] = [C + 1, 23];
        M.clp[`${C}_24`] = [C + 1, 23];
        // Bar separators also blocked while the CLP is closed.
        M.clp[`${C - 1}_16`] = [C + 1, 23];
        M.clp[`${C + 1}_16`] = [C + 1, 23];
        // "Stare separat vizibila": the CLP may close only once the whole bay is
        // visibly open — line separator, both bar separators AND the breaker.
        M.clpIsolators[`${C + 1}_23`] = [[C, 21], [C - 1, 16], [C + 1, 16], [C, 19]];
        // A generator bay breaker parallels the unit with the system.
        if (kind === "gen") M.parallelMsg[`${C}_19`] = `Sunt condiții de paralel între ${name} și sistem?`;
        // Maneuver order: bar sep -> line sep -> breaker; opening reversed.
        const pairMsg = `Nu se poate închide separatorul de bară de pe linia ${name} dacă celălalt separator de bară este închis!`;
        const openMsg = `Nu se poate deschide separatorul de bară de pe linia ${name} dacă separatorul de linie este închis!`;
        M.pair[`${C - 1}_16`] = { cell: [C + 1, 16], msg: pairMsg };
        M.pair[`${C + 1}_16`] = { cell: [C - 1, 16], msg: pairMsg };
        M.closeRequires[`${C}_21`] = { cells: [[C - 1, 16], [C + 1, 16]], msg: `Nu se poate închide separatorul de linie de pe linia ${name} dacă separatorul de bară nu este închis!` };
        M.closeRequires[`${C}_19`] = { cells: [[C, 21]], msg: `Nu se poate închide întreruptorul de pe linia ${name} dacă separatorul de linie nu este închis!` };
        M.openRequires[`${C - 1}_16`] = { cells: [[C, 21]], msg: openMsg };
        M.openRequires[`${C + 1}_16`] = { cells: [[C, 21]], msg: openMsg };
        regCell(name, {
            barSeps: [{ bar: "I", cell: [C - 1, 16] }, { bar: "II", cell: [C + 1, 16] }],
            lineSep: [C, 21], breaker: [C, 19], clp: [C + 1, 23],
            generator: kind === "gen" ? [C, 24] : undefined,
        });
        put(C - 1, 18, CORNER_TR); put(C, 18, DERIV, 0); put(C + 1, 18, CORNER_TL);
        put(C, 19, INTR);
        put(C, 20, COND, 90);
        put(C, 21, SEP);                                        // line separator
        put(C, 22, DERIV, 270); put(C + 1, 22, CORNER_BL);      // CLP branch (right)
        put(C + 1, 23, CLP);
        put(C, 23, kind === "gen" ? COND : S["conductor_scurt_inalta_tensiune"].imageSource, 90);   // generators: full conductor; consumers: short stub
        put(C, 24, kind === "gen" ? GEN : TRAFO, 0);
        // Terminal arrow: generator = source arrow up (into the machine),
        // consumer = sink arrow down (out to the load). Label hangs below it.
        put(C, 25, kind === "gen" ? ARROW : ARROW_FINAL, kind === "gen" ? 270 : 90, lbl2(name, cel, 12));
    };

    // ---- Measuring cell, upper side: cell -> separator -> CLP branch -> bar I ----
    const cmTop = (C: number, side: "left" | "right", name: string, cel: string) => {
        const clpX = side === "left" ? C - 1 : C + 1;
        const barLine = "I" + sec(C);               // C.M. IB / C.M. I A tap bar I
        M.clp[`${C}_6`] = [clpX, 8];                 // own disconnector blocked while its CLP is closed
        M.clpBarLine[`${clpX}_8`] = barLine;         // this CLP earths busbar `barLine`
        M.barLineClp[barLine] = [clpX, 8];
        regSep(C, 6, barLine);                       // the C.M. disconnector taps this busbar
        regCell(name, { barSeps: [{ bar: "I", cell: [C, 6] }], clp: [clpX, 8] });
        put(C, 4, CEL_MAS, 0, lbl2(name, cel, -11));
        put(C, 5, COND, 90);
        put(C, 6, SEP);
        if (side === "left") {
            put(C, 7, DERIV, 90); put(C - 1, 7, CORNER_BR); put(C - 1, 8, CLP);
        } else {
            put(C, 7, DERIV, 270); put(C + 1, 7, CORNER_BL); put(C + 1, 8, CLP);
        }
        for (let y = 8; y <= 12; y++) put(C, y, COND, 90);
        put(C, BUS_TOP, DERIV, 180);
    };

    // ---- Measuring cell, lower side: bar II -> CLP branch -> separator -> cell ----
    // CLP sits BEFORE the line separator (between the bar and the separator).
    const cmBottom = (C: number, side: "left" | "right", name: string, cel: string) => {
        const clpX = side === "left" ? C - 1 : C + 1;
        const barLine = "II" + sec(C);              // C.M. II B / C.M. II A tap bar II
        M.clp[`${C}_18`] = [clpX, 17];              // own disconnector blocked while its CLP is closed
        M.clpBarLine[`${clpX}_17`] = barLine;       // this CLP earths busbar `barLine`
        M.barLineClp[barLine] = [clpX, 17];
        regSep(C, 18, barLine);                     // the C.M. disconnector taps this busbar
        regCell(name, { barSeps: [{ bar: "II", cell: [C, 18] }], clp: [clpX, 17] });
        put(C, BUS_MID, DERIV, 0);
        putHop(C, BUS_BOT);                     // crosses bar I bottom run
        // CLP branch right after the bar, before the separator
        if (side === "left") {
            put(C, 16, DERIV, 90); put(C - 1, 16, CORNER_BR); put(C - 1, 17, CLP);
        } else {
            put(C, 16, DERIV, 270); put(C + 1, 16, CORNER_BL); put(C + 1, 17, CLP);
        }
        put(C, 17, COND, 90);
        put(C, 18, SEP);                        // line separator (now below the CLP)
        put(C, 19, COND, 90);                   // extra conductor down to the cell
        // Flipped 180: bottom-side cell, so the connection lead points up (to the
        // bay) and the measuring circles hang below (C.M. II B / C.M. II A).
        put(C, 20, CEL_MAS, 180, lbl2(name, cel, 12));
    };

    // Bays spaced at a pitch of 4 columns (1 empty column between adjacent
    // bays) on the 53-column canvas so the cells breathe and the scheme
    // fills a wide screen.

    // ================= B (left) section =================
    topBay(3, "Trafo 4T", "CEL 27K");
    // LEA reserve feeder (single-bar) now sits at col 11; TSI-2 at col 16.
    topBay(11, "LEA Rezerva Donalam", "CEL 23K", { singleBar: true });
    topBay(16, "TSI-2", "CEL 21K");
    cmTop(22, "left", "C.M. IB", "CEL 30K");   // aligned above C.M. II B (col 22)

    bottomBay(2, "oe", "OE4", "CEL 28K");
    bottomBay(6, "oe", "LEA Donalam", "CEL 26K");
    bottomBay(10, "oe", "OE2", "CEL 24K");
    bottomBay(14, "ctv", "C.T.V - B", "CEL 22K");
    bottomBay(18, "gen", "GENERATOR 1", "CEL 20K");
    cmBottom(22, "left", "C.M. II B", "CEL 30K");

    // ================= A (right) section =================
    // Spaced at pitch 8, mirroring the B top bays (3/11/19) with a -1 offset
    // (B tops sit one column right of their pair; A tops one column left) so
    // STATIA A breathes the same as STATIA B without hitting OE1's bar-end.
    cmTop(34, "right", "C.M. I A", "CEL 2K");   // aligned above C.M. II A (col 34)
    // TSI-1 (9K) left, Trafo T1 (3K) right — matches the SLD order in STATIA A.
    topBay(41, "TSI-1", "CEL 9K");
    topBay(49, "Trafo T1", "CEL 3K");

    cmBottom(34, "right", "C.M. II A", "CEL 2K");
    bottomBay(38, "gen", "GENERATOR 2", "CEL 12K");
    bottomBay(42, "ctv", "C.T.V - A", "CEL 10K");
    bottomBay(46, "oe", "OE3", "CEL 8K");
    bottomBay(50, "oe", "OE1", "CEL 4K", { endRight: true });

    // ===== Couplings: both legs of a C.L. share one column per side (26 on
    //       B, 30 on A), breaker centred between them, so the three bars end
    //       aligned. The legs hop over the bar I top run without contact.

    // C.L. II B - II A: couples bar II sections over the top. Breaker sits on
    // row 5 (aligned with the trafo breakers), one conductor cell shorter.
    put(24, 5, CORNER_BR);
    put(25, 5, COND, 0);
    put(26, 5, INTR, 90, lbl2("C.L. II B - II A", "CEL 13K - 17K", -11));
    put(27, 5, COND, 0);
    put(28, 5, CORNER_BL);
    for (let y = 6; y <= 11; y++) { put(24, y, COND, 90); put(28, y, COND, 90); }
    put(24, 12, SEP); put(28, 12, SEP);
    regSep(24, 12, "IIB"); regSep(28, 12, "IIA");   // C.L. II B - II A bar disconnectors
    M.breakers["24_12"] = [[26, 5]]; M.breakers["28_12"] = [[26, 5]];
    M.closeRequires["26_5"] = { cells: [[24, 12], [28, 12]], all: true, msg: "Nu se poate închide întreruptorul de pe cupla C.L. II B - II A dacă separatoarele nu sunt închise!" };
    M.parallelMsg["26_5"] = "Sunt condiții de paralel între bara II B și bara II A?";
    regCell("C.L. II B - II A", { barSeps: [{ bar: "II", cell: [24, 12] }, { bar: "II", cell: [28, 12] }], breaker: [26, 5] });
    put(24, BUS_TOP, COND, 90); put(28, BUS_TOP, COND, 90); // legs pass the bar I gap as plain vertical lines
    put(24, BUS_MID, CORNER_TL);            // end of bar IIB
    put(28, BUS_MID, CORNER_TR);            // start of bar IIA

    // C.L. I B - I A: couples bar I sections under the bottom.
    put(24, BUS_BOT, CORNER_BL);            // end of bar IB (bottom run)
    put(24, 16, SEP);
    regSep(24, 16, "IB");                   // C.L. I B - I A bar disconnector (B side)
    for (let y = 17; y <= 23; y++) put(24, y, COND, 90);
    put(24, 24, CORNER_TR);
    put(25, 24, COND, 0);
    put(26, 24, INTR, 90, lbl2("C.L. I B - I A", "CEL 14K - 18K", 12));
    put(27, 24, COND, 0);
    put(28, 24, CORNER_TL);
    for (let y = 17; y <= 23; y++) put(28, y, COND, 90);
    put(28, 16, SEP);
    regSep(28, 16, "IA");                   // C.L. I B - I A bar disconnector (A side)
    put(28, BUS_BOT, CORNER_BR);            // start of bar IA (bottom run)
    M.breakers["24_16"] = [[26, 24]]; M.breakers["28_16"] = [[26, 24]];
    M.closeRequires["26_24"] = { cells: [[24, 16], [28, 16]], all: true, msg: "Nu se poate închide întreruptorul de pe cupla C.L. I B - I A dacă separatoarele nu sunt închise!" };
    M.parallelMsg["26_24"] = "Sunt condiții de paralel între bara I B și bara I A?";
    regCell("C.L. I B - I A", { barSeps: [{ bar: "I", cell: [24, 16] }, { bar: "I", cell: [28, 16] }], breaker: [26, 24] });

    // ===== Bar I ties (top run <-> bottom run): closed rings on the outer edges =====
    put(0, BUS_TOP, CORNER_BR); put(0, BUS_MID, COND, 90); put(0, BUS_BOT, CORNER_TR);
    put(52, BUS_TOP, CORNER_BL); put(52, BUS_MID, COND, 90); put(52, BUS_BOT, CORNER_TL);

    // ===== Bar labels: stacked at the aligned inner ends, just above
    //       their own line (like the SRS drawing) =====
    put(20, BUS_TOP, COND, 0, lbl1("IB", 52, 0));
    put(20, BUS_MID, COND, 0, lbl1("IIB", 52, 0));
    put(20, BUS_BOT, COND, 0, lbl1("IB", 52, 0));
    put(32, BUS_TOP, COND, 0, lbl1("IA", -48, 0));
    put(32, BUS_MID, COND, 0, lbl1("IIA", -48, 0));
    put(32, BUS_BOT, COND, 0, lbl1("IA", -48, 0));

    // ===== Fill the bar runs around the junctions placed above =====
    const fillH = (row: number, from: number, to: number) => {
        for (let x = from; x <= to; x++) if (!used.has(`${x}_${row}`)) put(x, row, COND, 0);
    };
    fillH(BUS_TOP, 0, 23);   // IB (top run) — ends under the leg hop at 24
    fillH(BUS_MID, 2, 23);   // IIB — ends at the leg corner (24)
    fillH(BUS_BOT, 0, 23);   // IB (bottom run) — ends at the leg corner (24)
    fillH(BUS_TOP, 29, 51);  // IA (top run) — from the leg hop (28) to the ring at 52
    fillH(BUS_MID, 29, 50);  // IIA — from the leg corner (28) to the OE1 corner (51)
    fillH(BUS_BOT, 29, 51);  // IA (bottom run) — from the leg corner (28) to the ring at 52

    // Apply the whole-scheme DY shift to the interlock tables: the layout
    // above authored keys/cells in natural rows, but clicks read the shifted
    // grid rows, so every maneuver key + cell tuple is offset by DY here.
    const shiftKey = (k: string) => { const [x, y] = k.split("_").map(Number); return `${x}_${y + DY}`; };
    const shiftCell = (c: [number, number]): [number, number] => [c[0], c[1] + DY];
    const remap = <T,>(rec: Record<string, T>, fn: (v: T) => T): Record<string, T> => {
        const out: Record<string, T> = {};
        for (const k in rec) out[shiftKey(k)] = fn(rec[k]);
        return out;
    };
    M.breakers = remap(M.breakers, (v) => v.map(shiftCell));
    M.clp = remap(M.clp, shiftCell);
    M.clpIsolators = remap(M.clpIsolators, (v) => v.map(shiftCell));
    M.pair = remap(M.pair, (v) => ({ ...v, cell: shiftCell(v.cell) }));
    M.closeRequires = remap(M.closeRequires, (v) => ({ ...v, cells: v.cells.map(shiftCell) }));
    M.openRequires = remap(M.openRequires, (v) => ({ ...v, cells: v.cells.map(shiftCell) }));
    M.parallelMsg = remap(M.parallelMsg, (v) => v);
    // clpBarLine / sepBarLine are keyed by cell (x_y) -> shift the key; barLineSeps
    // / barLineClp are keyed by busbar name -> keep the key, shift the cell values.
    M.clpBarLine = remap(M.clpBarLine, (v) => v);
    M.sepBarLine = remap(M.sepBarLine, (v) => v);
    for (const ln in M.barLineSeps) M.barLineSeps[ln] = M.barLineSeps[ln].map(shiftCell);
    for (const ln in M.barLineClp) M.barLineClp[ln] = shiftCell(M.barLineClp[ln]);

    if (DY) for (const id in cellMeta) {
        const m = cellMeta[id];
        cellMeta[id] = {
            barSeps: m.barSeps.map((b) => ({ ...b, cell: shiftCell(b.cell) })),
            lineSep: m.lineSep && shiftCell(m.lineSep),
            breaker: m.breaker && shiftCell(m.breaker),
            clp: m.clp && shiftCell(m.clp),
            generator: m.generator && shiftCell(m.generator),
        };
    }

    return { cellMeta, maneuvers: M };
}

// ---- State helpers shared by both modules ---------------------------------
// The per-cell switch expectations live in htScenarios (they are pure state
// logic); re-exported here so the modules can pull layout + rules from one
// place.
export { primaryBar, isCoupler, wantBarSepClosed, wantGenOn } from "./htScenarios";

// Reads the sign name currently rendered at (x, y) on page 1.
export const makeReadName = (useStore: any) => (x: number, y: number): string =>
    (useStore.getState?.().elements || []).find((s: any) => s.x === x && s.y === y && s.page === 1)?.names?.[0]?.name || "";

// The owning cell id of a switch at (x,y), for logging + unnecessary-maneuver.
export const ownerCellOf = (cellMeta: Record<string, CellSwitches>, x: number, y: number): string | null => {
    for (const id in cellMeta) {
        const m = cellMeta[id];
        const hit = [m.lineSep, m.breaker, m.clp, m.generator, ...m.barSeps.map((b) => b.cell)]
            .some((c) => c && c[0] === x && c[1] === y);
        if (hit) return id;
    }
    return null;
};
